import { FlexRow } from '@/components/common/Flex';
import Icon from '@/components/common/icons/Icon';
import { iconByShareStatus } from '@/components/goals/helpers';
import { useUserData } from '@/services/user';
import { Circle, forwardRef, SquareProps } from '@chakra-ui/react';
import { GoalType, ShareStatus } from '@common/graphql/generated';
import { toPx } from '@common/util/styles';
import { IconSVGComponentProps } from '../common/icons/IconComponent';
import { configByGoalType } from './config';

export interface GoalIconCircleProps extends Omit<SquareProps, 'color'> {
    type: GoalType;
    shareStatus?: ShareStatus;
    showShareStatusIcon?: boolean;
    iconSize?: IconSVGComponentProps['size'];
    inverted?: boolean;
}

const GoalIconCircle = forwardRef(
    (
        {
            type,
            size = 8,
            iconSize = 'md',
            inverted,
            shareStatus,
            showShareStatusIcon = true,
            ...props
        }: GoalIconCircleProps,
        ref
    ) => {
        const { activePartnershipId } = useUserData();
        const { iconName, color } = configByGoalType[type];
        return (
            <Circle
                bg={inverted ? 'white' : color}
                size={size}
                pos="relative"
                ref={ref}
                {...props}
            >
                <Icon
                    name={iconName}
                    size={iconSize}
                    color={inverted ? color : 'white'}
                />
                {showShareStatusIcon && activePartnershipId && shareStatus && (
                    <ShareStatusIcon shareStatus={shareStatus} />
                )}
            </Circle>
        );
    }
);

const SHARE_ICON_CIRCLE_OFFSET = -2;
const SHARE_ICON_CIRCLE_SIZE = 5;
const SHARE_ICON_CIRCLE_BORDER = SHARE_ICON_CIRCLE_SIZE * 2;
const SHARE_ICON_CIRCLE_PADDING = 1;
const SHARE_ICON_CIRCLE_OUTLINE_SIZE = SHARE_ICON_CIRCLE_PADDING * 2;
const SHARE_ICON_SIZE = 3;

const ShareStatusIcon = ({ shareStatus }: { shareStatus: ShareStatus }) => {
    const iconName = iconByShareStatus[shareStatus];

    return (
        <FlexRow
            pos="absolute"
            left={SHARE_ICON_CIRCLE_OFFSET}
            top={SHARE_ICON_CIRCLE_OFFSET}
            w={SHARE_ICON_CIRCLE_SIZE}
            h={SHARE_ICON_CIRCLE_SIZE}
            padding={SHARE_ICON_CIRCLE_PADDING}
            bg="gray.700"
            outline={`solid ${toPx(SHARE_ICON_CIRCLE_OUTLINE_SIZE)} white`}
            borderRadius={SHARE_ICON_CIRCLE_BORDER}
            alignItems="center"
            justifyContent="center"
        >
            <Icon name={iconName} w={SHARE_ICON_SIZE} color="white" />
        </FlexRow>
    );
};

export default GoalIconCircle;
